export type TaxClassTotal = {
  lsTaxId: string;
  amount: number;
  taxComponentName: string;
  rate: number;
  subtotal: number;
  taxableAmount: number;
  taxName: string;
};

// Utility type to transform fields to numbers
export type TaxClassTotalWithNumbers = Omit<
  TaxClassTotal,
  'amount' | 'rate' | 'subtotal' | 'taxableAmount'
> & {
  amount: number;
  rate: number;
  subtotal: number;
  taxableAmount: number;
};

export const generateTaxClassTotals = (taxes: Tax[]): TaxClassTotal[] => {
  const parseValue = (value: string) => parseFloat(value) || 0;

  // Step 1: Flatten and process individual tax entries
  const allTaxEntries = taxes.flatMap((tax: Tax) => {
    const taxClassObjects: TaxClassTotalWithNumbers[] = [];
    // Primary tax entry
    taxClassObjects.push({
      lsTaxId: tax.id,
      amount: Number(tax.amount),
      taxComponentName: tax.name,
      rate: Number(tax.rate),
      subtotal: Number(tax.subtotal),
      taxableAmount: Number(tax.taxable),
      taxName: tax.taxname,
    });

    // Secondary tax entry if available
    if (tax?.amount2 || tax?.rate2 || tax?.subtotal2 || tax?.taxname2) {
      taxClassObjects.push({
        lsTaxId: tax.id,
        amount: parseValue(tax.amount2),
        rate: parseValue(tax.rate2),
        subtotal: parseValue(tax.subtotal2),
        taxableAmount: parseValue(tax.taxable),
        taxName: tax.taxname2,
        taxComponentName: tax.name,
      });
    }

    return taxClassObjects;
  });

  const mergedTaxes = allTaxEntries.reduce(
    (
      acc: Record<string, TaxClassTotalWithNumbers>,
      tax: TaxClassTotalWithNumbers
    ): Record<string, TaxClassTotalWithNumbers> => {
      const key = `${tax.taxName}_${tax.rate}`; // Unique key based on taxName and rate
      if (!acc[key]) {
        acc[key] = { ...tax };
      } else {
        // Merge values
        acc[key].amount += tax.amount;
        acc[key].subtotal += tax.subtotal;
        acc[key].taxableAmount += tax.taxableAmount;
      }
      return acc;
    },
    {}
  );

  // Step 3: Convert final values to strings and assign to `this.taxClassTotals`
  return Object.values(mergedTaxes).map((tax: TaxClassTotalWithNumbers) => ({
    lsTaxId: tax.lsTaxId,
    amount: parseFloat(tax.amount.toString()),
    taxComponentName: tax.taxComponentName,
    rate: parseFloat(tax.rate.toString()),
    subtotal: parseFloat(tax.subtotal.toString()),
    taxableAmount: parseFloat(tax.taxableAmount.toString()),
    taxName: tax.taxName,
  }));
};
